// Shared portal shell — ports prototype/portal-shell.jsx into ES modules.
// Per design_handoff_rev9_portal/starter/CLAUDE.md: keep inline styles and
// CSS class strings as-is; they resolve via web/src/styles/globals.css.
//
// Conversion notes vs prototype:
//   - React/useState/etc. imported, not destructured from a global.
//   - Formatters imported from ../lib/format (not redefined).
//   - Nav uses useNavigate + useLocation instead of setView/view callbacks.
//   - Each component is a named export; no Object.assign(window, ...).

/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useMemo, useEffect } from 'react';

import { EntityDisclaimer } from './EntityDisclaimer';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { api } from '../lib/api';
import { useAuth, isN8User } from '../lib/auth';
import { fmtUSD } from '../lib/format';
import { usePreferences } from '../lib/preferences';

// ─── Atoms ──────────────────────────────────────────────────────────────────

export function BrandMark() {
  return <span className="brand-mark"><em style={{ fontStyle: 'italic' }}>9</em></span>;
}

export function Brand({ onClick }: { onClick?: () => void }) {
  return (
    <button className="brand" onClick={onClick} style={{ background: 'transparent', border: 0, padding: 0 }}>
      <BrandMark />
      <span className="brand-name">rev<em>9</em>&nbsp;capital</span>
    </button>
  );
}

export function Kicker({ children, style }: { children: any; style?: any }) {
  return <span className="page-kicker" style={style}>{children}</span>;
}

export function Pill({ children, tone }: { children: any; tone?: string }) {
  return <span className="pill" data-tone={tone || ''}>{children}</span>;
}

// Exported so the Holdings venue FILTER CHIPS use the same shortening as the
// pills they filter — otherwise a chip reads COINBASE EXCHANGE beside a pill
// reading COINBASE on the same screen (c319).
export function venueShortLabel(venue: string): string {
  return venue === 'Coinbase Exchange' ? 'COINBASE' : venue.toUpperCase();
}

export function VenuePill({ venue }: { venue: string }) {
  return <Pill>{venueShortLabel(venue)}</Pill>;
}

export function OwnershipPill({ ownership }: { ownership: string }) {
  return (
    <Pill tone={ownership === 'client-owned' ? 'ink' : 'accent'}>
      {ownership === 'client-owned' ? 'CLIENT-OWNED' : 'COMPANY-HELD'}
    </Pill>
  );
}

export function TypePill({ type }: { type: string }) {
  const tone =
    type === 'deposit' || type === 'buy' ? 'accent'
    : type === 'withdrawal' || type === 'sell' ? 'warn'
    : '';
  return <Pill tone={tone}>{type.toUpperCase()}</Pill>;
}

// Sub-nav between the two ad-hoc treasury surfaces (balances / activity).
// Styled to match the segmented toggles on those pages — hairline border,
// ink-filled active tab, no radius / no shadow (editorial hard rules). Lives
// here (shared) rather than in either page module so neither imports the other.
export function TreasuryTabs({ active }: { active: 'balances' | 'activity' }) {
  const tabs = [
    { key: 'balances', label: 'BALANCES', to: '/wallet-balances' },
    { key: 'activity', label: 'ACTIVITY', to: '/wallet-balances/activity' },
  ] as const;
  return (
    <div style={{ display: 'flex', border: 'var(--hairline)', width: 'fit-content', marginBottom: 20 }}>
      {tabs.map(t => (
        <Link
          key={t.key}
          to={t.to}
          className="label-s"
          style={{
            padding: '4px 14px', textDecoration: 'none',
            background: active === t.key ? 'var(--ink)' : 'transparent',
            color: active === t.key ? 'var(--paper)' : 'var(--ink)',
          }}
        >
          {t.label}
        </Link>
      ))}
    </div>
  );
}

export function Stat({ label, value, sub, large }: { label: string; value: any; sub?: any; large?: boolean }) {
  return (
    <div>
      <div className="stat-label">{label}</div>
      <div className="stat-num" style={{ fontSize: large ? 'clamp(48px, 6vw, 80px)' : undefined }}>
        {value}
      </div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

export function DollarStat({
  label, value, sub, large, decimals = 2,
}: {
  label: string; value: number; sub?: any; large?: boolean; decimals?: number;
}) {
  const formatted = fmtUSD(value, decimals);
  return (
    <div>
      <div className="stat-label">{label}</div>
      <div className="stat-num" style={{ fontSize: large ? 'clamp(64px, 7.5vw, 112px)' : undefined }}>
        <span className="currency">$</span>{formatted.slice(1)}
      </div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

export function Banner({
  kicker, body, action, tone,
}: {
  kicker: any; body: any; action?: any; tone?: string;
}) {
  return (
    <div className="banner" data-tone={tone || ''}>
      <div className="banner-kicker">{kicker}</div>
      <div className="banner-body">{body}</div>
      <div className="banner-action">{action}</div>
    </div>
  );
}

export function KickerRow({ kicker, action, onAction }: { kicker: any; action?: any; onAction?: () => void }) {
  return (
    <div className="kicker-row">
      <span className="kicker">{kicker}</span>
      {action && <a onClick={onAction} style={{ cursor: 'pointer' }}>{action}</a>}
    </div>
  );
}

export function AssetMark({ asset }: { asset: string }) {
  return <span className="asset-mark">{asset.slice(0, 3)}</span>;
}

// ─── Nav ────────────────────────────────────────────────────────────────────

// Fund-first manager nav (16.d.1): the fund is the manager's home. The
// SMA-shaped pages (Dashboard/Holdings/Performance/Activity) are NOT top-level
// for a manager — they're client-scoped drill-downs reached via the roster, and
// a manager hitting one without ?client_id= redirects to `/fund` (16.e.5 repointed
// the fallbacks off the now-folded `/clients`), so dropping them strands no one.
// `Investors` → the standalone fund investor roster (16.d.3.3). The firm-wide
// SMA roster at `/clients` was FOLDED (16.e.5): no manager-facing entry point;
// reachable by direct URL + the super-user credential-admin back-button.
const MANAGER_NAV = [
  { id: 'fund',       label: 'Fund',       path: '/fund' },
  { id: 'investors',  label: 'Investors',  path: '/investors' },
  { id: 'recon',      label: 'Recon',      path: '/recon' },
  { id: 'statements', label: 'Statements', path: '/statements' },
  // Operator-review finding (James, 2026-07-06): clicking this landed on
  // /fund with no indication why — disabled + labeled instead of a dead
  // link until the feature actually ships.
  { id: 'documents',  label: 'Documents',  path: '/documents', disabled: true },
  { id: 'wallet-balances', label: 'Wallets', path: '/wallet-balances' },
] as const;
// Client nav (unchanged) — the investor-facing portfolio pages.
const NAV_ITEMS = [
  { id: 'dashboard',   label: 'Dashboard',   path: '/dashboard' },
  { id: 'fund',        label: 'Fund',        path: '/fund' },
  { id: 'holdings',    label: 'Holdings',    path: '/holdings' },
  { id: 'performance', label: 'Performance', path: '/performance' },
  { id: 'activity',    label: 'Activity',    path: '/activity' },
  { id: 'statements',  label: 'Statements',  path: '/statements' },
  { id: 'documents',   label: 'Documents',   path: '/documents', disabled: true },
] as const;

const ACCOUNT_MENU = [
  { label: 'Account settings', path: '/settings' },
  { label: 'Audit log',        path: '/audit' },
  { label: 'Contact',          path: '/portal/contact' },
] as const;

export function Nav({ client, onSignOut, role }: {
  client: { shortName: string };
  onSignOut?: () => void;
  role?: 'manager' | 'client' | 'auditor';
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const { setAppearance } = usePreferences();
  const { user } = useAuth();

  // Preserve ?client_id= across every nav click — managers can be
  // "viewing as" a specific client (Slice 14 fix); without this every
  // nav click bounces them back to /clients.
  const currentSearch = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    const out = new URLSearchParams();
    const clientId = sp.get('client_id');
    if (clientId) out.set('client_id', clientId);
    const s = out.toString();
    return s ? `?${s}` : '';
  }, [location.search]);

  const withSearch = (path: string) => path + currentSearch;
  const active = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');
  // The "Wallets" (treasury) item is N8-internal — hide it for anyone whose
  // account email isn't @n8.capital (mirrors the backend IsN8Capital gate).
  const items = (role === 'manager' ? MANAGER_NAV : NAV_ITEMS)
    .filter(it => it.id !== 'wallet-balances' || isN8User(user));

  return (
    <header className="nav">
      <div className="nav-inner">
        <Brand onClick={() => navigate(withSearch('/dashboard'))} />
        <nav className="nav-links">
          {items.map(it => (
            'disabled' in it && it.disabled ? (
              <span
                key={it.id}
                className="nav-link"
                aria-disabled="true"
                style={{ opacity: 0.45, cursor: 'default' }}
                title="Coming soon"
              >
                {it.label} <span className="label-s" style={{ fontSize: 10 }}>(coming soon)</span>
              </span>
            ) : (
              <Link
                key={it.id}
                to={withSearch(it.path)}
                className="nav-link"
                data-active={active(it.path) ? '1' : '0'}
              >
                {it.label}
              </Link>
            )
          ))}
        </nav>
        <div className="nav-right" style={{ position: 'relative' }}>
          <button className="account-menu" onClick={() => setMenuOpen(o => !o)}>
            <span className="dot" />
            {client.shortName}
            <span style={{ marginLeft: 4, opacity: 0.5 }}>▾</span>
          </button>
          {menuOpen && (
            <>
              <div onClick={() => setMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 60 }} />
              <div style={{
                position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                background: 'var(--paper)', border: '1px solid var(--ink)',
                minWidth: 240, zIndex: 70, padding: '8px 0',
              }}>
                {ACCOUNT_MENU.map(it => (
                  <button
                    key={it.path}
                    onClick={() => { navigate(withSearch(it.path)); setMenuOpen(false); }}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '10px 18px', border: 0, background: 'transparent',
                      fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.14em',
                      textTransform: 'uppercase', color: 'var(--ink-2)', cursor: 'pointer',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--rule-soft)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    {it.label}
                  </button>
                ))}
                {user?.is_superuser && (
                  <button
                    onClick={() => { navigate('/admin/impersonate'); setMenuOpen(false); }}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '10px 18px', border: 0, background: 'transparent',
                      fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.14em',
                      textTransform: 'uppercase', color: 'var(--ink-2)', cursor: 'pointer',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--rule-soft)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    View as account →
                  </button>
                )}
                <hr className="rule" style={{ margin: '8px 0' }} />
                <button
                  onClick={() => { setAppearance('clean'); setMenuOpen(false); }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '10px 18px', border: 0, background: 'transparent',
                    fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.14em',
                    textTransform: 'uppercase', color: 'var(--ink-2)', cursor: 'pointer',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--rule-soft)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  Switch to clean view →
                </button>
                <hr className="rule" style={{ margin: '8px 0' }} />
                <button
                  onClick={() => { onSignOut?.(); setMenuOpen(false); navigate('/sign-in'); }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '10px 18px', border: 0, background: 'transparent',
                    fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.14em',
                    textTransform: 'uppercase', color: 'var(--ink-2)', cursor: 'pointer',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--rule-soft)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  Sign out →
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}


// ─── CSV export link ────────────────────────────────────────────────────────
// Streams the same scoped data the page shows, as a download. Preserves
// ?client_id= (manager impersonation) and any current filters.
export function ExportCsvLink({ endpoint, clientId, params }: {
  endpoint: 'holdings' | 'activity' | 'performance';
  clientId?: string | null;
  params?: Record<string, string>;
}) {
  const qs = new URLSearchParams(params || {});
  if (clientId) qs.set('client_id', clientId);
  qs.set('export', 'csv');
  return (
    <a className="btn btn-ghost" href={`/api/v1/portfolio/${endpoint}?${qs.toString()}`}
       download style={{ textDecoration: 'none' }}>
      Export CSV
    </a>
  );
}

// ─── CleanSidebar (clean-mode left rail) ────────────────────────────────────
// Professional SaaS layout: vertical nav rail + account section docked at the
// bottom. Reuses the same nav item sets + ?client_id= preservation as Nav so
// manager impersonation keeps working. Only rendered when appearance==='clean'.

export function CleanSidebar({ client, onSignOut, role }: {
  client: { shortName: string };
  onSignOut?: () => void;
  role?: 'manager' | 'client' | 'auditor';
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { setAppearance } = usePreferences();
  const { user } = useAuth();

  const currentSearch = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    const clientId = sp.get('client_id');
    return clientId ? `?client_id=${clientId}` : '';
  }, [location.search]);

  const withSearch = (path: string) => path + currentSearch;
  const active = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');
  // The "Wallets" (treasury) item is N8-internal — hide it for anyone whose
  // account email isn't @n8.capital (mirrors the backend IsN8Capital gate).
  const items = (role === 'manager' ? MANAGER_NAV : NAV_ITEMS)
    .filter(it => it.id !== 'wallet-balances' || isN8User(user));

  return (
    <aside className="clean-sidebar">
      <button className="clean-brand" onClick={() => navigate(withSearch('/dashboard'))}>
        <BrandMark />
        <span className="clean-brand-name">rev<em>9</em> capital</span>
      </button>

      <nav className="clean-nav">
        {items.map(it => (
          'disabled' in it && it.disabled ? (
            <span
              key={it.id}
              className="clean-nav-link"
              aria-disabled="true"
              style={{ opacity: 0.45, cursor: 'default' }}
              title="Coming soon"
            >
              {it.label} <span className="label-s" style={{ fontSize: 10 }}>(coming soon)</span>
            </span>
          ) : (
            <Link
              key={it.id}
              to={withSearch(it.path)}
              className="clean-nav-link"
              data-active={active(it.path) ? '1' : '0'}
            >
              {it.label}
            </Link>
          )
        ))}
      </nav>

      <div className="clean-sidebar-foot">
        {ACCOUNT_MENU.map(it => (
          <Link
            key={it.path}
            to={withSearch(it.path)}
            className="clean-nav-link"
            data-active={active(it.path) ? '1' : '0'}
          >
            {it.label}
          </Link>
        ))}
        {user?.is_superuser && (
          <button className="clean-nav-link" onClick={() => navigate('/admin/impersonate')}>
            View as account →
          </button>
        )}
        <button className="clean-nav-link" onClick={() => setAppearance('editorial')}>
          Editorial view →
        </button>
        <button
          className="clean-nav-link"
          onClick={() => { onSignOut?.(); navigate('/sign-in'); }}
        >
          Sign out →
        </button>
        <div className="clean-sidebar-acct">
          <span className="dot" /> {client.shortName}
        </div>
      </div>
    </aside>
  );
}

// Segmented Editorial / Clean control for the Settings page (reuses the
// existing .filter-chip look; no new component, no UI library).
export function AppearanceToggle() {
  const { appearance, setAppearance } = usePreferences();
  return (
    <div className="filter-bar" style={{ marginBottom: 0 }}>
      <button
        className="filter-chip"
        data-active={appearance === 'editorial' ? '1' : '0'}
        onClick={() => setAppearance('editorial')}
      >
        Editorial
      </button>
      <button
        className="filter-chip"
        data-active={appearance === 'clean' ? '1' : '0'}
        onClick={() => setAppearance('clean')}
      >
        Clean
      </button>
    </div>
  );
}

// ─── ViewingAs (manager impersonation indicator) ────────────────────────────
// Shows under the Nav whenever a manager has ?client_id= in the URL.
// Makes the impersonation context discoverable + gives a one-click exit.

export function ViewingAsBanner({ clientName }: { clientName?: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  if (!clientName) return null;
  const stop = () => {
    // Drop ?client_id= but keep the rest of the search (none currently, but
    // future-proof).
    const sp = new URLSearchParams(location.search);
    sp.delete('client_id');
    const rest = sp.toString();
    navigate(location.pathname + (rest ? `?${rest}` : ''), { replace: true });
  };
  return (
    <div style={{
      borderBottom: 'var(--hairline)',
      padding: '10px var(--pad-x)',
      background: 'var(--tint)',
      fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.14em',
      textTransform: 'uppercase', color: 'var(--ink-2)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16,
    }}>
      <span style={{ color: 'var(--accent)' }}>VIEWING ·</span>
      <strong style={{ color: 'var(--ink)' }}>{clientName}</strong>
      <span style={{ color: 'var(--ink-3)' }}>(manager impersonation)</span>
      <button
        onClick={stop}
        className="btn-link"
        style={{ marginLeft: 8, fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.14em' }}
      >
        × Stop
      </button>
    </div>
  );
}

// ─── ImpersonationBanner (super-user "view as" overlay — slice 13.a) ─────────
// Distinct from ViewingAsBanner above: that is a manager's ?client_id= DATA
// scoping (still the manager's own identity). THIS is a true super-user identity
// overlay — request.user IS the target — so it's rendered in oxblood/warn tone
// to be unmissable and is driven by the server-set `impersonated_by` marker on
// /auth/me. While it shows, all writes are 403'd server-side (read-only).

export function ImpersonationBanner() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [exiting, setExiting] = useState(false);
  const actor = user?.impersonated_by;
  if (!actor) return null;
  const targetName = user?.display_name || user?.email || 'this account';
  const exit = async () => {
    setExiting(true);
    try { await api('/auth/impersonate/stop', { method: 'POST' }); }
    catch { /* already stopped / session gone — refresh resolves true state */ }
    try {
      await refreshUser();
      setExiting(false);
      navigate('/dashboard', { replace: true });
    } catch {
      // /auth/me failed — never strand the operator on a stale overlay with a
      // dead "Exiting…" button; hard-reload so AuthProvider re-probes the true
      // session on mount.
      window.location.assign('/dashboard');
    }
  };
  return (
    <div style={{
      borderBottom: '2px solid var(--oxblood)',
      padding: '10px var(--pad-x)',
      background: 'var(--oxblood-tint)',
      fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.14em',
      textTransform: 'uppercase', color: 'var(--ink-2)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16,
    }}>
      <span style={{ color: 'var(--oxblood)' }}>VIEWING AS ·</span>
      <strong style={{ color: 'var(--ink)' }}>{targetName}</strong>
      <span style={{ color: 'var(--ink-3)' }}>read-only · {actor.email}</span>
      <button
        onClick={exit}
        disabled={exiting}
        className="btn-link"
        style={{ marginLeft: 8, fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.14em', color: 'var(--oxblood)' }}
      >
        {exiting ? 'Exiting…' : '× Exit'}
      </button>
    </div>
  );
}

// ─── Footer (portal) ───────────────────────────────────────────────────────

export function Footer() {
  return (
    <footer style={{
      borderTop: 'var(--hairline)',
      padding: '28px var(--pad-x) 36px',
      maxWidth: 'var(--max)',
      margin: '0 auto', width: '100%',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16,
        fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.16em',
        textTransform: 'uppercase', color: 'var(--ink-3)',
      }}>
        {/* adviser-status label removed — the last LC-REV9-001 holding-out
            instance; it sat directly above the entity disclaimer contradicting it */}
        <div>Rev9 Capital LLC</div>
        <div>Holdings &amp; balances reconciled daily against venue records</div>
        <div>© 2026 Rev9 Capital · Miami, FL</div>
      </div>
      <p style={{
        marginTop: 18, maxWidth: '110ch',
        fontFamily: 'var(--sans)', fontSize: 11, lineHeight: 1.55, color: 'var(--ink-3)',
      }}>
        <strong style={{ color: 'var(--ink-2)' }}>Important information.</strong>{' '}
        Figures are unaudited and subject to final reconciliation. Digital assets are not bank deposits,
        are not FDIC- or SIPC-insured, are not guaranteed, and may lose value, including the total loss of
        principal. Digital-asset markets are highly volatile; past performance does not guarantee future
        results. This portal is for informational purposes and is not an offer, solicitation, or tax,
        legal, or investment advice.
      </p>
      <EntityDisclaimer style={{ maxWidth: '110ch' }} />
    </footer>
  );
}

// ─── Stale-data banner ─────────────────────────────────────────────────────

export function StaleBanner({ asOf }: { asOf: string }) {
  return (
    <div style={{
      borderBottom: 'var(--hairline)',
      padding: '10px var(--pad-x)',
      maxWidth: 'var(--max)',
      margin: '0 auto',
      fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.18em',
      textTransform: 'uppercase', color: 'var(--oxblood)',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    }}>
      <span>STALE DATA · LAST RECONCILED {asOf}</span>
      <span>Contact support if numbers look wrong</span>
    </div>
  );
}

/**
 * Item 30 — a treasury page's headline figure is WRONG when a source is down,
 * and until now the only sign of that sat at the very bottom, below a
 * sparkline and three tables. The operator read an authoritative-looking total
 * and learned it was short only if they scrolled.
 *
 * Shaped like `StaleBanner` above deliberately: the design spec prescribes a
 * banner for stale/unreconciled data ("do not silently display data that
 * hasn't reconciled"), and this is the house implementation of that row. NOT
 * the `.banner` class — that is a 220px-kicker / serif-italic / action grid
 * built for one editorial sentence, used twice in the whole app.
 *
 * Deliberately a SUMMARY, not the list. The detail stays at the bottom where
 * it already reads well; this line's only job is to qualify the number before
 * the eye reaches it.
 */
export function SourcesUnavailableBanner({ down }: { down: number }) {
  if (down <= 0) return null;
  return (
    <div style={{
      borderBottom: 'var(--hairline)',
      padding: '10px 0',
      marginBottom: 20,
      fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.18em',
      textTransform: 'uppercase', color: 'var(--oxblood)',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      gap: 12, flexWrap: 'wrap',
    }}>
      {/* No denominator: the activity payload carries no count of sources
          attempted, and inventing one would be a fabricated figure on a page
          whose whole point is not overstating what it knows. */}
      <span>{down} {down === 1 ? 'source' : 'sources'} unavailable &mdash; treasury figures are incomplete</span>
      <span>See the detail at the foot of this page</span>
    </div>
  );
}

// ─── Page header ───────────────────────────────────────────────────────────

export function PageHeader({ kicker, title, meta }: { kicker: any; title: any; meta?: any }) {
  return (
    <header className="page-head">
      <div>
        <Kicker>{kicker}</Kicker>
        <h1 className="page-title">{title}</h1>
      </div>
      {meta && <div className="page-head-meta">{meta}</div>}
    </header>
  );
}

// ─── Sortable table primitive ──────────────────────────────────────────────
// Columns: [{ key, label, align?, render?, sortBy? }]

export interface SortColumn {
  key: string;
  label: any;
  align?: 'left' | 'right' | 'center';
  render?: (row: any) => any;
  sortBy?: (row: any) => any;
}

export function SortTable({
  columns, rows, defaultSort, defaultDir = 'desc', emptyMessage,
}: {
  columns: SortColumn[];
  rows: any[];
  defaultSort?: string;
  defaultDir?: 'asc' | 'desc';
  emptyMessage?: string;
}) {
  const [sortKey, setSortKey] = useState<string | undefined>(defaultSort);
  const [dir, setDir] = useState<'asc' | 'desc'>(defaultDir);

  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    const col = columns.find(c => c.key === sortKey);
    const get = col?.sortBy || ((r: any) => r[sortKey]);
    return [...rows].sort((a, b) => {
      const av = get(a), bv = get(b);
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return dir === 'asc' ? av - bv : bv - av;
      return dir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
  }, [rows, sortKey, dir, columns]);

  const onSort = (key: string) => {
    if (sortKey === key) setDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setDir('desc'); }
  };

  return (
    <table className="tbl">
      <thead>
        <tr>
          {columns.map(c => (
            <th
              key={c.key}
              data-align={c.align}
              data-sorted={sortKey === c.key ? '1' : '0'}
              onClick={() => onSort(c.key)}
              style={{ cursor: 'pointer' }}
            >
              {c.label}
              <span className="sort">{sortKey === c.key ? (dir === 'asc' ? '↑' : '↓') : '↕'}</span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sorted.length === 0 && (
          <tr>
            <td colSpan={columns.length} style={{
              padding: '48px 12px', textAlign: 'center',
              color: 'var(--ink-3)', fontStyle: 'italic', fontFamily: 'var(--serif)',
            }}>{emptyMessage || 'No records to show.'}</td>
          </tr>
        )}
        {sorted.map((r, i) => (
          <tr key={r.id || i}>
            {columns.map(c => (
              <td key={c.key} data-align={c.align}>
                {c.render ? c.render(r) : r[c.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Suppress unused import warning if useEffect isn't used in this build
void useEffect;
